// Regras da tela de Processos (telas/Processos.jsx). Funções puras, sem I/O.

export const LIMITE_COMUM_MB = 3;      // igual a arquivo.upload.max-size do back
export const LIMITE_ASSINADO_MB = 15;  // igual a processo.upload.max-size-assinado do back
export const MB = 1024 * 1024;
export const TAMANHOS_DE_PAGINA = [10, 20, 50];
export const TIPOS_ACEITOS = ['application/pdf', 'image/jpeg', 'image/png'];

export function formatarTamanho(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < MB) return `${Math.round(n / 1024)} KB`;
  return `${(n / MB).toFixed(1).replace('.', ',')} MB`;
}

// Mesma regra do back (ProcessoService.temAssinaturaDigital): PDF com dicionário de assinatura (/Sig) e o
// intervalo de bytes que ela cobre (/ByteRange). Recebe os bytes do arquivo (Uint8Array).
export function temAssinaturaDigital(bytes) {
  if (!bytes || bytes.length < 5) return false;
  const inicio = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
  if (inicio !== '%PDF') return false;
  return contem(bytes, '/ByteRange') && contem(bytes, '/Sig');
}

function contem(bytes, trecho) {
  const alvo = Array.from(trecho, (c) => c.charCodeAt(0));
  const primeiro = alvo[0];
  for (let i = bytes.indexOf(primeiro); i !== -1 && i <= bytes.length - alvo.length; i = bytes.indexOf(primeiro, i + 1)) {
    let igual = true;
    for (let j = 1; j < alvo.length; j++) {
      if (bytes[i + j] !== alvo[j]) { igual = false; break; }
    }
    if (igual) return true;
  }
  return false;
}

// O que fazer com um arquivo escolhido para o processo:
//  'invalido'     — tipo não aceito
//  'direto'       — comum até 3 MB: envia como está
//  'otimizar'     — comum acima de 3 MB: pergunta se otimiza (ou se é assinado)
//  'assinado'     — PDF assinado até 15 MB: envia intacto
//  'grandeDemais' — PDF assinado acima de 15 MB: não tem como enviar (não pode ser alterado)
export function classificarArquivo({ tipo, tamanho }, assinado) {
  if (!TIPOS_ACEITOS.includes(tipo)) return 'invalido';
  if (assinado) return tamanho > LIMITE_ASSINADO_MB * MB ? 'grandeDemais' : 'assinado';
  return tamanho > LIMITE_COMUM_MB * MB ? 'otimizar' : 'direto';
}

// Parâmetros da lista (GET /processos): só os filtros preenchidos
export function parametrosDaLista(filtros, pagina, tamanho) {
  const p = { pagina, tamanho };
  if (filtros.inicio) p.inicio = filtros.inicio;
  if (filtros.fim) p.fim = filtros.fim;
  if (filtros.placa && filtros.placa.trim()) p.placa = filtros.placa.trim();
  if (filtros.cliente && filtros.cliente.trim()) p.cliente = filtros.cliente.trim();
  if (filtros.status) p.status = filtros.status;
  return p;
}

// Contador da página (o que está na tela, até 50): total, com upload (verde) e pendentes de upload (vermelho)
export function resumoDaPagina(itens) {
  const lista = itens || [];
  const enviados = lista.filter((p) => p.status === 'ENVIADO').length;
  return { total: lista.length, enviados, pendentes: lista.length - enviados };
}

// Data local AAAA-MM-DD (toISOString usa UTC e, à noite, já seria amanhã)
export function dataLocal(data = new Date()) {
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
}

// "2026-10-05" + 1 -> "2026-10-06" (meio-dia evita pular dia no horário de verão)
export function somarDias(iso, dias) {
  const [a, m, d] = iso.split('-').map(Number);
  return dataLocal(new Date(a, m - 1, d + dias, 12));
}

// O filtro é "um dia só" (De = Até, nada mais preenchido)? Devolve o dia; senão null
export function diaDoFiltro(filtros) {
  const f = filtros || {};
  const soData = !(f.placa || '').trim() && !(f.cliente || '').trim() && !f.status;
  return soData && f.inicio && f.inicio === f.fim ? f.inicio : null;
}

// "11 a 20 de 45"
export function faixaDaPagina(pagina, tamanho, total) {
  if (!total) return '0 de 0';
  const de = pagina * tamanho + 1;
  const ate = Math.min(total, (pagina + 1) * tamanho);
  return `${de} a ${ate} de ${total}`;
}

export function formatarDocumento(doc) {
  const d = String(doc || '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc || '';
}

export function formatarData(iso) {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return d && m && a ? `${d}/${m}/${a}` : String(iso);
}

// Busca de cliente pelo nome (lupa do formulário). O back (/clientes/nome) procura o texto inteiro, então
// "claudia braga" não acharia "CLAUDIA DE AVILA BRAGA": a tela busca pela maior palavra e confere as demais com
// nomeConfere. Sem palavra de 3 letras ou mais, não há o que buscar ([]).
export function palavrasDoNome(nome) {
  const palavras = String(nome || '').toLowerCase().split(/\s+/).filter(Boolean);
  return palavras.some((p) => p.length >= 3) ? palavras : [];
}

const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Todas as palavras aparecem no nome, em qualquer ordem, sem diferenciar acento nem maiúscula
export function nomeConfere(nomeCliente, palavras) {
  const nome = semAcento(nomeCliente);
  return palavras.every((p) => nome.includes(semAcento(p)));
}

// Nome do arquivo otimizado: "processo.jpg" -> "processo.pdf"; em partes: "processo (parte 1 de 2).pdf"
export function nomeOtimizado(original, parte, totalPartes) {
  const base = String(original || 'documento').replace(/\.[^.]+$/, '');
  return totalPartes > 1 ? `${base} (parte ${parte} de ${totalPartes}).pdf` : `${base}.pdf`;
}
